import { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

/* ---------------------------
   Types
   --------------------------- */

export interface ScoreDetails {
    sous_valorisation: number;
    qualite_financiere: number;
    stabilite_dividendes: number;
    liquidite: number;
    impact_diversification: number;
}

export interface ImpactDiversification {
    sector: string | null;
    country: string | null;
}

export interface RebalancingSuggestion {
    action: 'OPPORTUNITÉ' | 'TENDANCE_HAUSSIÈRE' | 'SUREXPOSITION' | 'ÉQUILIBRÉ';
    symbole: string;
    nom_societe: string;
    score_value_tilt: number | null;
    decote_estimee: number | null;
    poids_actuel: number | null;
    poids_cible: number | null;
    montant_suggere: number | null;
    pourcentage_suggere: number | null;
    justification: string;
    impact_diversification: ImpactDiversification;
}

export interface EligibleStock {
    symbole: string;
    nom_societe: string;
    secteur: string;
    pays: string;
    score_value_tilt: number | null;
    decote_estimee: number | null;
    score_details: ScoreDetails | null;
}

export interface PortfolioSummary {
    total_value: number;
    portfolio_value: number;
    cash_available: number;
    cash_percentage: number;
    holdings_count: number;
    target_holdings: string;
}

export interface AllocationData {
    by_sector: Record<string, number>;
    by_country: Record<string, number>;
}

export interface AllocationImpact {
    current: Record<string, number>;
    projected: Record<string, number>;
}

export interface RebalancingData {
    generated_at: string;
    portfolio_summary: PortfolioSummary;
    current_allocation: AllocationData;
    suggestions: RebalancingSuggestion[];
    sector_impact: AllocationImpact;
    country_impact: AllocationImpact;
    eligible_stocks: EligibleStock[];
    disclaimer: string;
    message?: string;
}

/* ---------------------------
   Hook
   --------------------------- */

export const useRebalancing = () => {
    const { token } = useAuth();
    const [data, setData] = useState<RebalancingData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if (!token) {
            setLoading(false);
            setError('Non authentifié');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/portfolio/rebalancing`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Session expirée. Veuillez vous reconnecter.');
                }
                throw new Error(`Erreur serveur: ${response.status}`);
            }

            const result: RebalancingData = await response.json();
            setData(result);
        } catch (err) {
            console.error('Error fetching rebalancing data:', err);
            setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return { data, loading, error, refetch: fetchData };
};
